import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import FakeInput from 'components/Common/Form/Input/FakeInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import TimeInput from 'components/Common/Form/Input/TimeInput'
import Group from 'components/utils/Group'
import { addDays, convertTZ, startOfDay } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import ExperienceTransferBookingHotelSelect from './ExperienceTransferBookingHotelSelect'
import { GoogleAutocompletePlaceItem } from 'api/search'
import moment from 'moment'

const StretchedGroup = styled(Group)`
  > * {
    flex: 1;
  }
`

interface Props {
  transfer: App.ExperienceTransferView
  name: string
  disableLocations?: boolean
  onChange?: (transfer: App.ExperienceTransferView) => void
  experience: App.ExperienceOffer
}

function ExperienceTransferBookingForm(props: Props) {
  const {
    name,
    transfer,
    disableLocations,
    experience,
    onChange = noop,
  } = props

  const intl = useIntl()

  // A transfer experience should always have an airport
  const airport = experience.airport!

  const minDate = useMemo(() => {
    const cutoffDays = ((experience.bookingCutoffHours ?? 0) / 24) + 1
    return addDays(startOfDay(new Date()), cutoffDays)
  }, [experience.bookingCutoffHours],
  )

  const onHotelChange = useCallback((option: GoogleAutocompletePlaceItem) => {
    onChange({
      ...transfer,
      hotel: {
        name: option.mainText,
        kmFromAirport: option.distanceMeters ? Math.ceil(option.distanceMeters / 1000) : undefined,
        googlePlaceId: option.placeId,
      },
    })
  }, [onChange, transfer])

  const onDateChange = useCallback((date: Date) => {
    // need this value only for pick up
    if (name !== 'toHotel') return
    onChange({
      ...transfer,
      date: date && moment(date).format('YYYY-MM-DD'),
    })
  }, [onChange, transfer, name])

  const expirationDate = useMemo(() => {
    if (experience.expirationDate) {
      const expirationDate = new Date(experience.expirationDate)
      if (!experience.location?.timezone) {
        return expirationDate
      }
      return convertTZ(expirationDate, experience.location.timezone)
    }
  }, [experience.expirationDate, experience.location?.timezone])

  const fromHotelLabel = intl.formatMessage({
    id: '5d7e1PEctc',
    defaultMessage: 'From (Hotel)',
    description: 'Label for the hotel origin input in transfer booking form',
  })
  const hotelPickUpPlaceholder = intl.formatMessage({
    id: 'jTIe6tvYeB',
    defaultMessage: 'Hotel pick up',
    description: 'Placeholder for the hotel pick up input in transfer booking form',
  })
  const hotelPickUpError = intl.formatMessage({
    id: 'wXWv6HP2S4',
    defaultMessage: 'Please specify your hotel pick up',
    description: 'Validation error when hotel pick up location is not provided',
  })
  const toAirportLabel = intl.formatMessage({
    id: 'UkKCqRSv+i',
    defaultMessage: 'To (Airport)',
    description: 'Label for the airport destination input in transfer booking form',
  })
  const fromAirportLabel = intl.formatMessage({
    id: '7OQM+9BduX',
    defaultMessage: 'From (Airport)',
    description: 'Label for the airport origin input in transfer booking form',
  })
  const toHotelLabel = intl.formatMessage({
    id: '2wp2Z8EihT',
    defaultMessage: 'To (Hotel)',
    description: 'Label for the hotel destination input in transfer booking form',
  })
  const hotelDropOffPlaceholder = intl.formatMessage({
    id: 'F0ySDZFCeY',
    defaultMessage: 'Hotel drop off',
    description: 'Placeholder for the hotel drop off input in transfer booking form',
  })
  const hotelDropOffError = intl.formatMessage({
    id: 'KZlOB2x3Q+',
    defaultMessage: 'Please specify your hotel drop off',
    description: 'Validation error when hotel drop off location is not provided',
  })
  const flightNumberLabel = intl.formatMessage({
    id: '8b7hg6rupi',
    defaultMessage: 'Flight number',
    description: 'Label for the flight number input in transfer booking form',
  })
  const flightNumberPlaceholder = intl.formatMessage({
    id: 'HDXIcTSKme',
    defaultMessage: 'e.g. QF123',
    description: 'Placeholder example for flight number input',
  })
  const arrivalDateLabel = intl.formatMessage({
    id: 'Va6og6uMC6',
    defaultMessage: 'Arrival date',
    description: 'Label for arrival date input in airport-to-hotel transfer form',
  })
  const departureDateLabel = intl.formatMessage({
    id: 'RByemg1/cI',
    defaultMessage: 'Departure date',
    description: 'Label for departure date input in hotel-to-airport transfer form',
  })
  const arrivalTimeLabel = intl.formatMessage({
    id: 'becB19Jp4g',
    defaultMessage: 'Arrival time',
    description: 'Label for arrival time input in airport-to-hotel transfer form',
  })
  const departureTimeLabel = intl.formatMessage({
    id: 'm/KXunn3sd',
    defaultMessage: 'Departure time',
    description: 'Label for departure time input in hotel-to-airport transfer form',
  })
  const arrivalDateError = intl.formatMessage({
    id: 'OFNVABC9Zv',
    defaultMessage: 'Please provide date of arrival',
    description: 'Validation error when arrival date is not provided',
  })
  const departureDateError = intl.formatMessage({
    id: 'dRxamBraou',
    defaultMessage: 'Please provide date of departure',
    description: 'Validation error when departure date is not provided',
  })
  const arrivalTimeError = intl.formatMessage({
    id: 'gyeylEJ0fy',
    defaultMessage: 'Please provide time of arrival',
    description: 'Validation error when arrival time is not provided',
  })
  const departureTimeError = intl.formatMessage({
    id: '83+EIM/O2L',
    defaultMessage: 'Please provide time of departure',
    description: 'Validation error when departure time is not provided',
  })
  const selectTimePlaceholder = intl.formatMessage({
    id: 'NM1GYLaycx',
    defaultMessage: 'Select time',
    description: 'Placeholder for the time selection input in transfer booking form',
  })

  return <>
    <StretchedGroup tabletDirection="horizontal" direction="vertical" gap={12}>
      <input type="hidden" name={`${name}.type`} value={transfer.type} />
      {transfer.type === 'HOTEL-TO-AIRPORT' && <>
        <ExperienceTransferBookingHotelSelect
          label={fromHotelLabel}
          required
          name={`${name}.hotel`}
          placeholder={hotelPickUpPlaceholder}
          disabled={disableLocations}
          transfer={transfer}
          onChange={onHotelChange}
          requiredErrorMessage={hotelPickUpError}
          airport={airport}
        />
        <FakeInput
          label={toAirportLabel}
          value={`${airport.name} (${airport.code})`}
          required
          disabled
        />
      </>}
      {transfer.type == 'AIRPORT-TO-HOTEL' && <>
        <FakeInput
          label={fromAirportLabel}
          value={`${airport.name} (${airport.code})`}
          required
          disabled
        />
        <ExperienceTransferBookingHotelSelect
          label={toHotelLabel}
          required
          name={`${name}.hotel`}
          placeholder={hotelDropOffPlaceholder}
          transfer={transfer}
          disabled={disableLocations}
          onChange={onHotelChange}
          airport={airport}
          requiredErrorMessage={hotelDropOffError}
        />
      </>}
    </StretchedGroup>
    <StretchedGroup tabletDirection="horizontal" direction="vertical" gap={12}>
      <TextInput
        label={flightNumberLabel}
        defaultValue={transfer.flightNumber}
        name={`${name}.flightNumber`}
        placeholder={flightNumberPlaceholder}
      />
      <DatePickerInput
        label={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalDateLabel : departureDateLabel}
        required
        min={minDate}
        max={expirationDate}
        defaultValue={transfer.date}
        name={`${name}.date`}
        requiredErrorMessage={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalDateError : departureDateError}
        onDateChange={onDateChange}
      />
      <TimeInput
        label={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalTimeLabel : departureTimeLabel}
        defaultValue={transfer.time}
        name={`${name}.time`}
        placeholder={selectTimePlaceholder}
        step={15}
        requiredErrorMessage={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalTimeError : departureTimeError}
      />
    </StretchedGroup>
  </>
}

export default ExperienceTransferBookingForm
